
import { ExternalLink, FileImage } from "lucide-react";
import { useState } from "react";
import ManualThumbnailDialog from "@/components/dashboard/ManualThumbnailDialog";
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator
} from "@/components/ui/context-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { getContentUrl, isHighlightedText } from "@/components/dashboard/content-card/contentCardUtils";

interface ContentPreviewProps {
  contentItem: any;
  onUpdate?: () => void;
}

const ContentPreview = ({ contentItem, onUpdate }: ContentPreviewProps) => {
  const [showThumbnailDialog, setShowThumbnailDialog] = useState(false);
  
  // Safely check if thumbnail is manually set
  const isManualThumbnail = contentItem?.metadata && 
    typeof contentItem.metadata === 'object' && 
    contentItem.metadata?.thumbnail_source === 'manual';
  
  // Check if this is highlighted text content
  const isHighlight = isHighlightedText(contentItem);
  
  // Prevent any navigation, just update the dialog state
  const handleOpenThumbnailDialog = () => {
    setShowThumbnailDialog(true);
  };
  
  // Handle opening the URL in a new tab, using deep link for highlighted text
  const handleOpenContent = (e: React.MouseEvent) => {
    // Stop event propagation to prevent any parent click handlers
    e.preventDefault();
    e.stopPropagation();
    
    // Use deep link URL for highlighted text, regular URL otherwise
    const targetUrl = getContentUrl(contentItem);
    console.log(`[NAVIGATION DEBUG] ContentPreview - Opening content URL in new tab: ${targetUrl}`);
    
    // Check for text fragment support and show appropriate messaging
    if (isHighlight && contentItem.metadata?.highlight_data?.textFragment) {
      const supportsTextFragments = 'fragmentDirective' in document;
      if (!supportsTextFragments) {
        toast.info("Your browser doesn't support text fragments. You'll be taken to the page, but may need to find the highlighted text manually.");
      }
    }
    
    window.open(targetUrl, "_blank", "noopener");
  };
  
  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger>
          <div 
            className="aspect-video bg-clustr-lightGray rounded-lg overflow-hidden flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity relative"
            onClick={handleOpenContent}
          >
            {contentItem.thumbnail_url ? (
              <img
                src={contentItem.thumbnail_url}
                alt={contentItem.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            ) : contentItem.content_type === "image" ? (
              <img
                src={contentItem.url}
                alt={contentItem.title}
                className="w-full h-full object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <div className="text-clustr-darkGray flex flex-col items-center">
                <ExternalLink size={48} strokeWidth={1} className="mb-2" />
                <span className="text-center px-4">
                  {isHighlight ? "Click to view highlighted text" : "Click to open content"}
                </span>
              </div>
            )}
            
            {/* Add the thumbnail button in the upper right corner */}
            <Button
              variant="outline"
              size="icon"
              className={cn(
                "absolute top-2 right-2 bg-white bg-opacity-80 hover:bg-white hover:bg-opacity-100 z-10",
                isManualThumbnail && "text-blue-500 hover:text-blue-600"
              )}
              onClick={(e) => {
                e.stopPropagation(); // Prevent the parent div's click event
                handleOpenThumbnailDialog();
              }}
            >
              <FileImage className="h-4 w-4" />
            </Button>
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem onClick={handleOpenThumbnailDialog}>
            <FileImage className={cn(
              "mr-2 h-4 w-4",
              isManualThumbnail && "text-blue-500"
            )} />
            Set manual thumbnail
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
      
      <ManualThumbnailDialog 
        contentId={contentItem.id}
        contentTitle={contentItem.title}
        open={showThumbnailDialog}
        onOpenChange={setShowThumbnailDialog}
      />
    </>
  );
};

export default ContentPreview;
